'use client';

import { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import {
  Activity, BarChart3, Building2, FileText, LayoutDashboard, LayoutGrid, LogOut,
  Megaphone, Menu, Phone, RefreshCw, Shield, Users, X,
} from 'lucide-react';
import { ToastProvider } from '@/components/admin/Toast';
import { OverviewPanel } from './panels/OverviewPanel';
import { AnalyticsPanel } from './panels/AnalyticsPanel';
import { TenantsPanel } from './panels/TenantsPanel';
import { LandingPanel } from './panels/LandingPanel';
import { PlatformUsersPanel } from './panels/PlatformUsersPanel';
import { VoicePanel } from './panels/VoicePanel';
import { PlansPanel } from './panels/PlansPanel';
import { BroadcastsPanel } from './panels/BroadcastsPanel';
import { AuditPanel } from './panels/AuditPanel';
import { GlobalSearch } from './GlobalSearch';
import { SystemPanel } from './panels/SystemPanel';
import type {
  IntegrationService, PlatformAdminUser, PlatformAlert, PlatformHealth,
  PlatformOverview, RecentActivity, SuperTab, TenantRow,
} from './types';

const TAB_META: Record<SuperTab, { title: string; subtitle: string; path: string }> = {
  overview: { title: 'Visao geral', subtitle: 'Metricas e alertas da plataforma', path: '/super-admin' },
  analytics: { title: 'Analytics', subtitle: 'Pedidos e receita por tenant', path: '/super-admin/analytics' },
  tenants: { title: 'Tenants', subtitle: 'Criar, gerir e impersonar restaurantes', path: '/super-admin/tenants' },
  landing: { title: 'Landing page', subtitle: 'Ordem e visibilidade em okai.pt', path: '/super-admin/landing' },
  voice: { title: 'Voz e telefonia', subtitle: 'DIDs por tenant', path: '/super-admin/voz' },
  plans: { title: 'Planos', subtitle: 'Billing e limites por tenant', path: '/super-admin/planos' },
  broadcasts: { title: 'Comunicados', subtitle: 'Mensagens aos admins de restaurante', path: '/super-admin/comunicados' },
  platform: { title: 'Admins plataforma', subtitle: 'Contas super-admin', path: '/super-admin/plataforma' },
  audit: { title: 'Auditoria', subtitle: 'Registo de acoes na plataforma', path: '/super-admin/auditoria' },
  system: { title: 'Sistema', subtitle: 'Saude e integracoes', path: '/super-admin/sistema' },
};

const NAV: Array<{ id: SuperTab; label: string; icon: React.ReactNode }> = [
  { id: 'overview', label: 'Visao geral', icon: <LayoutDashboard className="w-5 h-5" /> },
  { id: 'analytics', label: 'Analytics', icon: <BarChart3 className="w-5 h-5" /> },
  { id: 'tenants', label: 'Tenants', icon: <Building2 className="w-5 h-5" /> },
  { id: 'landing', label: 'Landing', icon: <LayoutGrid className="w-5 h-5" /> },
  { id: 'voice', label: 'Voz', icon: <Phone className="w-5 h-5" /> },
  { id: 'plans', label: 'Planos', icon: <Shield className="w-5 h-5" /> },
  { id: 'broadcasts', label: 'Comunicados', icon: <Megaphone className="w-5 h-5" /> },
  { id: 'platform', label: 'Admins', icon: <Users className="w-5 h-5" /> },
  { id: 'audit', label: 'Auditoria', icon: <FileText className="w-5 h-5" /> },
  { id: 'system', label: 'Sistema', icon: <Activity className="w-5 h-5" /> },
];

type Session = { user_id: string; email: string; name: string; role: string };

function SuperAdminDashboard({ activeTab }: { activeTab: SuperTab }) {
  const router = useRouter();
  const meta = TAB_META[activeTab];
  const [session, setSession] = useState<Session | null>(null);
  const [overview, setOverview] = useState<PlatformOverview | null>(null);
  const [tenants, setTenants] = useState<TenantRow[]>([]);
  const [platformUsers, setPlatformUsers] = useState<PlatformAdminUser[]>([]);
  const [health, setHealth] = useState<PlatformHealth | null>(null);
  const [activity, setActivity] = useState<RecentActivity | null>(null);
  const [alerts, setAlerts] = useState<PlatformAlert[]>([]);
  const [integrations, setIntegrations] = useState<IntegrationService[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [mobileNav, setMobileNav] = useState(false);

  const loadSession = useCallback(async () => {
    const res = await fetch('/api/super-admin/auth/me');
    const data = await res.json();
    if (!data.authenticated) {
      router.push('/super-admin/auth');
      return false;
    }
    setSession(data.session);
    return true;
  }, [router]);

  const loadData = useCallback(async () => {
    setRefreshing(true);
    setLoadError('');
    try {
      const [ovRes, tRes, pRes, hRes, aRes, alRes, intRes] = await Promise.all([
        fetch('/api/super-admin/overview'),
        fetch('/api/super-admin/tenants'),
        fetch('/api/super-admin/platform-users'),
        fetch('/api/super-admin/health'),
        fetch('/api/super-admin/activity'),
        fetch('/api/super-admin/alerts'),
        fetch('/api/super-admin/integrations'),
      ]);
      if (tRes.status === 401 || ovRes.status === 401) {
        router.push('/super-admin/auth');
        return;
      }
      const failedResponses = [ovRes, tRes, pRes, hRes, aRes, alRes, intRes]
        .filter((response) => !response.ok);
      if (failedResponses.length > 0) {
        setLoadError('Alguns dados da plataforma não ficaram disponíveis. Podes voltar a tentar sem perder o contexto.');
      }
      const ovData = ovRes.ok ? await ovRes.json() : { data: null };
      const tData = tRes.ok ? await tRes.json() : { data: [] };
      const pData = pRes.ok ? await pRes.json() : { data: [] };
      const hData = hRes.ok ? await hRes.json() : { data: null };
      const aData = aRes.ok ? await aRes.json() : { data: null };
      const alData = alRes.ok ? await alRes.json() : { data: [] };
      const intData = intRes.ok ? await intRes.json() : { data: { services: [] } };
      setOverview(ovData.data ?? null);
      setTenants((tData.data ?? []) as TenantRow[]);
      setPlatformUsers((pData.data ?? []) as PlatformAdminUser[]);
      setHealth(hData.data ?? null);
      setActivity(aData.data ?? null);
      setAlerts(alData.data ?? []);
      setIntegrations(intData.data?.services ?? []);
    } catch {
      setLoadError('Não foi possível atualizar os dados da plataforma. Verifica a ligação e tenta novamente.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [router]);

  useEffect(() => {
    (async () => {
      const ok = await loadSession();
      if (ok) await loadData();
    })();
  }, [loadSession, loadData]);

  useEffect(() => {
    setMobileNav(false);
  }, [activeTab]);

  const logout = async () => {
    await fetch('/api/super-admin/auth/logout', { method: 'POST' });
    router.push('/super-admin/auth');
  };

  return (
    <div className="sa-layout">
      <aside className={`sa-sidebar ${mobileNav ? 'sa-sidebar-open' : ''}`} aria-label="Navegação da plataforma">
        <div className="sa-sidebar-logo">
          <div className="sa-sidebar-logo-mark"><Shield className="w-5 h-5" /></div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-white truncate">Okai Platform</p>
            <p className="text-[11px] text-white/50">Super Admin</p>
          </div>
          <button type="button" className="sa-sidebar-close lg:hidden" onClick={() => setMobileNav(false)} aria-label="Fechar navegação">
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav className="sa-sidebar-nav">
          {NAV.map((item) => (
            <Link key={item.id} href={TAB_META[item.id].path} onClick={() => setMobileNav(false)}
              className={`sa-nav-item ${activeTab === item.id ? 'sa-nav-item-active' : ''}`}
              aria-current={activeTab === item.id ? 'page' : undefined}>
              <span className="sa-nav-icon">{item.icon}</span>
              {item.label}
              {item.id === 'tenants' && tenants.length > 0 && (
                <span className="sa-nav-badge">{tenants.length}</span>
              )}
              {item.id === 'overview' && alerts.some((a) => a.severity === 'critical') && (
                <span className="sa-nav-badge">!</span>
              )}
            </Link>
          ))}
        </nav>
        <div className="sa-sidebar-footer">
          {session && (
            <div className="sa-user-pill">
              <div className="sa-user-avatar">{session.name.charAt(0).toUpperCase()}</div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-white truncate">{session.name}</p>
                <p className="text-[10px] text-white/50 truncate">{session.email}</p>
              </div>
            </div>
          )}
          <button type="button" onClick={logout} className="sa-nav-item w-full">
            <LogOut className="w-5 h-5" /> Sair
          </button>
        </div>
      </aside>

      {mobileNav && <button type="button" className="sa-backdrop" onClick={() => setMobileNav(false)} aria-label="Fechar menu" />}

      <div className="sa-main-wrap">
        <header className="sa-topbar">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <button
              type="button"
              className="sa-menu-btn lg:hidden"
              onClick={() => setMobileNav(true)}
              aria-label="Abrir navegação"
              aria-expanded={mobileNav}
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="min-w-0 hidden sm:block">
              <h1 className="sa-topbar-title">{meta.title}</h1>
              <p className="sa-topbar-sub">{meta.subtitle}</p>
            </div>
            <div className="flex-1 max-w-md hidden md:block">
              <GlobalSearch tenants={tenants} />
            </div>
          </div>
          <button
            type="button"
            onClick={loadData}
            disabled={refreshing}
            aria-busy={refreshing}
            className="btn btn-secondary btn-sm"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
        </header>

        <main className="sa-content">
          {loadError && (
            <div className="mb-4 flex flex-col gap-3 rounded-xl border border-warning/30 bg-warning-soft px-4 py-3 text-sm text-warning-text sm:flex-row sm:items-center sm:justify-between" role="alert">
              <span>{loadError}</span>
              <button type="button" className="btn btn-secondary btn-sm flex-shrink-0" onClick={loadData}>
                Tentar novamente
              </button>
            </div>
          )}
          {activeTab === 'overview' && (
            <OverviewPanel overview={overview} tenants={tenants} alerts={alerts} loading={loading} />
          )}
          {activeTab === 'analytics' && <AnalyticsPanel />}
          {activeTab === 'tenants' && <TenantsPanel tenants={tenants} loading={loading} onRefresh={loadData} />}
          {activeTab === 'landing' && <LandingPanel tenants={tenants} loading={loading} onRefresh={loadData} />}
          {activeTab === 'voice' && <VoicePanel tenants={tenants} loading={loading} />}
          {activeTab === 'plans' && <PlansPanel tenants={tenants} onRefresh={loadData} />}
          {activeTab === 'broadcasts' && <BroadcastsPanel />}
          {activeTab === 'platform' && (
            <PlatformUsersPanel users={platformUsers} loading={loading} currentUserId={session?.user_id} onRefresh={loadData} />
          )}
          {activeTab === 'audit' && <AuditPanel />}
          {activeTab === 'system' && (
            <SystemPanel health={health} activity={activity} integrations={integrations} loading={loading} onRefresh={loadData} />
          )}
        </main>
      </div>
    </div>
  );
}

export default function SuperAdminContent({ activeTab }: { activeTab: SuperTab }) {
  return (
    <ToastProvider>
      <SuperAdminDashboard activeTab={activeTab} />
    </ToastProvider>
  );
}
